/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 * See LICENSE file in the project root for full license text
 */

import { v4 as uuidv4 } from 'uuid'
import { db } from '../core/storage/database'
import type { VersionConfig, VersionConfigResponse } from '../core/models/VersionConfig'
import { DEFAULT_VERSION_CONFIG } from '../core/models/VersionConfig'
import { apiClient, HTTPError } from '../lib/api/client'

// ============================================================================
// Helpers
// ============================================================================

function clampSpeed(speed: number): number {
  return Math.max(0.5, Math.min(2.0, speed))
}

async function getTextIdForVersion(versionId: string): Promise<string | null> {
  const version = await db.textVersions.get(versionId)
  return version?.textId ?? null
}

function serverConfigToLocal(server: VersionConfigResponse, existingId?: string): VersionConfig {
  return {
    id: existingId ?? server.id,
    versionId: server.versionId,
    userId: server.userId,
    readStageDirections: server.readStageDirections,
    readSceneDirections: server.readSceneDirections,
    readStructure: server.readStructure,
    readPresentation: server.readPresentation ?? false,
    readingSpeed: clampSpeed(server.readingSpeed),
    userSpeed: clampSpeed(server.userSpeed ?? 1.0),
    userCharacterId: server.userCharacterId,
    hideUserLinesBefore: server.hideUserLinesBefore,
    hideUserLinesDuring: server.hideUserLinesDuring,
    hideUserLinesAfter: server.hideUserLinesAfter,
    readUserLinesBefore: server.readUserLinesBefore,
    readUserLinesAfter: server.readUserLinesAfter,
    skipLongScenes: server.skipLongScenes ?? false,
    abbreviateLongLines: server.abbreviateLongLines ?? false,
    syncStatus: 'SYNCED',
    updatedAt: server.updatedAt,
    createdAt: server.createdAt,
  }
}

// ============================================================================
// getOrCreateConfig
// ============================================================================

/**
 * Retourne la config locale pour (versionId, userId).
 * Si absente, crée une config par défaut en IndexedDB avec syncStatus PENDING.
 * Ne fait pas d'appel réseau.
 */
export async function getOrCreateConfig(versionId: string, userId: string): Promise<VersionConfig> {
  const existing = await db.versionConfigs
    .where('[versionId+userId]')
    .equals([versionId, userId])
    .first()

  if (existing) return existing

  const now = new Date().toISOString()
  const config: VersionConfig = {
    ...DEFAULT_VERSION_CONFIG,
    id: uuidv4(),
    versionId,
    userId,
    createdAt: now,
    updatedAt: now,
    syncStatus: 'PENDING',
  }

  try {
    await db.versionConfigs.add(config)
  } catch {
    // Race condition : une autre instance a créé l'entrée entre le get et le add
    // (ex: sync + montage du composant en parallèle) → relire l'existant
    const race = await db.versionConfigs
      .where('[versionId+userId]')
      .equals([versionId, userId])
      .first()
    if (race) return race
    // Si toujours absent, propager
    throw new Error(`versionConfig introuvable après ConstraintError (${versionId}/${userId})`)
  }
  return config
}

// ============================================================================
// updateConfig
// ============================================================================

/**
 * Met à jour la config locale pour (versionId, userId).
 * Incrémente updatedAt, passe syncStatus à PENDING.
 * Si en ligne, tente un push non-bloquant vers le serveur.
 */
export async function updateConfig(
  versionId: string,
  userId: string,
  changes: Partial<
    Omit<VersionConfig, 'id' | 'versionId' | 'userId' | 'createdAt' | 'updatedAt' | 'syncStatus'>
  >,
  isOnline: boolean = navigator.onLine
): Promise<VersionConfig> {
  const base = await getOrCreateConfig(versionId, userId)

  // Clamper la vitesse si fournie
  const safeChanges = { ...changes }
  if (safeChanges.readingSpeed !== undefined) {
    safeChanges.readingSpeed = clampSpeed(safeChanges.readingSpeed)
  }
  if (safeChanges.userSpeed !== undefined) {
    safeChanges.userSpeed = clampSpeed(safeChanges.userSpeed)
  }

  const now = new Date().toISOString()
  const updated: VersionConfig = {
    ...base,
    ...safeChanges,
    updatedAt: now,
    syncStatus: 'PENDING',
  }

  await db.versionConfigs.put(updated)

  if (isOnline) {
    // Fire-and-forget : les PENDING seront resynchronisés au prochain cycle
    _pushConfig(updated).catch(() => {
      // Silencieux — resync via pushPendingConfigs
    })
  }

  return updated
}

// ============================================================================
// pushPendingConfigs
// ============================================================================

/**
 * Pousse toutes les configs PENDING d'un utilisateur vers le serveur.
 * Appelée par le SyncService lors d'un cycle de sync.
 */
export async function pushPendingConfigs(userId: string, isOnline: boolean): Promise<void> {
  if (!isOnline) return

  const pending = await db.versionConfigs
    .where('syncStatus')
    .equals('PENDING')
    .filter((c) => c.userId === userId)
    .toArray()

  await Promise.allSettled(
    pending.map(async (config) => {
      try {
        const serverConfig = await _pushConfig(config)
        // Push réussi (200) — marquer SYNCED avec les données serveur
        const synced: VersionConfig = {
          ...serverConfigToLocal(serverConfig, config.id),
        }
        await db.versionConfigs.put(synced)
      } catch (err: unknown) {
        if (_is409(err)) {
          // Serveur plus récent → appliquer LWW serveur
          const serverData = _extract409Body(err)
          if (serverData) {
            await applyServerConfig(serverData)
          }
        }
        // Autres erreurs : conserver PENDING pour le prochain cycle
      }
    })
  )
}

// ============================================================================
// applyServerConfig
// ============================================================================

/**
 * Applique une config serveur en respectant LWW.
 * - Absente localement → créer avec syncStatus SYNCED
 * - Présente et serveur plus récent → remplacer
 * - Présente et locale plus récente → conserver (sera poussée par pushPendingConfigs)
 */
export async function applyServerConfig(serverConfig: VersionConfigResponse): Promise<void> {
  const local = await db.versionConfigs
    .where('[versionId+userId]')
    .equals([serverConfig.versionId, serverConfig.userId])
    .first()

  if (!local) {
    await db.versionConfigs.put(serverConfigToLocal(serverConfig))
    return
  }

  if (serverConfig.updatedAt > local.updatedAt) {
    await db.versionConfigs.put(serverConfigToLocal(serverConfig, local.id))
  }
  // local.updatedAt >= serverConfig.updatedAt → conserver la locale (plus récente ou égale)
}

// ============================================================================
// Internals
// ============================================================================

async function _pushConfig(config: VersionConfig): Promise<VersionConfigResponse> {
  const textId = await getTextIdForVersion(config.versionId)
  if (!textId) throw new Error(`textId not found for versionId ${config.versionId}`)

  const body: Record<string, unknown> = {
    updated_at: config.updatedAt,
    read_stage_directions: config.readStageDirections,
    read_scene_directions: config.readSceneDirections,
    read_structure: config.readStructure,
    read_presentation: config.readPresentation,
    reading_speed: config.readingSpeed,
    user_speed: config.userSpeed,
    user_character_id: config.userCharacterId ?? null,
    hide_user_lines_before: config.hideUserLinesBefore,
    hide_user_lines_during: config.hideUserLinesDuring,
    hide_user_lines_after: config.hideUserLinesAfter,
    read_user_lines_before: config.readUserLinesBefore,
    read_user_lines_after: config.readUserLinesAfter,
    skip_long_scenes: config.skipLongScenes,
    abbreviate_long_lines: config.abbreviateLongLines,
  }

  const response = await apiClient.put<VersionConfigResponse>(
    `/texts/${textId}/versions/${config.versionId}/config`,
    body
  )
  return response
}

function _is409(err: unknown): boolean {
  return err instanceof HTTPError && err.status === 409
}

function _extract409Body(err: unknown): VersionConfigResponse | null {
  if (!(err instanceof HTTPError)) return null
  try {
    // err.error est de type APIError { detail: string | object }
    // Le backend retourne detail = config.to_dict() (objet) sur 409
    const raw = err.error as { detail?: unknown } | undefined
    const detail = raw?.detail
    if (detail && typeof detail === 'object' && 'versionId' in detail) {
      return detail as VersionConfigResponse
    }
    // Fallback : detail peut être une string JSON
    if (typeof detail === 'string') {
      const parsed: unknown = JSON.parse(detail)
      if (parsed && typeof parsed === 'object' && 'versionId' in parsed) {
        return parsed as VersionConfigResponse
      }
    }
  } catch {
    // Silencieux — conserver PENDING pour le prochain cycle
  }
  return null
}

export const versionConfigService = {
  getOrCreateConfig,
  updateConfig,
  pushPendingConfigs,
  applyServerConfig,
}
